import React, { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import {
  Grid,
  Toolbar,
  Button,
  Typography,
  MenuItem,
  Select,
} from "@material-ui/core";
import logo from "../../assets/votpng.png";
import WalletButton from "./components/WalletButton";
import cvxLogo from "../../assets/cvxLogo.png";
import votBoxOutlined from "../../assets/votBoxOutlined.png";
import crvToken from "../../assets/crvToken.png";
import { ethers } from "ethers";
import { delegationStatus, getWeightSyncStatus, syncDelegation, pollForReceipt } from "../../scripts/scripts";
import { useStylesDark, useStylesLight } from "../../Styles/MobileHeaderStyles";
import { Link } from "react-router-dom";
import fxnLogo from "../../assets/fxnLogo.png";
import ybTokenLogo from "../../assets/tokens/yb.png";
import VoteCalculator from "../../Pages/Auction/Components/VoteCalculator";

const menuItems = [
  {
    value: "/",
    name: "Curve",
    logo: cvxLogo,
    smallLogo: crvToken,
  },
  { value: "/fxn", name: "f(x)", logo: cvxLogo, smallLogo: fxnLogo },
  { value: "/yb", name: "YB", logo: cvxLogo, smallLogo: ybTokenLogo },
];

const Header = (props) => {
  const classesLight = useStylesLight();
  const classesDark = useStylesDark();
  const [classes, setClasses] = useState(classesDark);
  const isYB =
    typeof window !== "undefined" && window.location.pathname === "/yb";
  const [delegateText, setDelegateText] = useState("Optimize Rewards");
  const [buttonText, setButtonText] = useState("Claim Rewards");
  const [addressLockedRef, setAddressLockedRef] = useState(null);
  const [refreshAddressCheck, setRefreshAddressCheck] = useState(0);
  const [connectedNetwork, setConnectedNetwork] = useState(0);
  const [addressPendingAmount, setaddressPendingAmount] =
    useState("Loading...");
  const [addressVotingAmount, setAddressVotingAmount] = useState("Loading...");
  const [refreshDB, setRefreshDB] = useState(0);
  const [totalLockedRef, setTotalLockedRef] = useState();
  const [totalLockedAmount, setTotalLockedAmount] = useState("Loading...");
  const [totalveCRVRef, setTotalveCRVRef] = useState();
  const [totalveCRVAmount, setTotalveCRVAmount] = useState("0.00");
  const [totalDelegatedRef, setTotalDelegatedRef] = useState();
  const [totalDelegatedAmount, setTotalDelegatedAmount] =
    useState("Loading...");
  const {
    loading: delegationLoading,
    isVotiumCurrent,
    isVotiumFuture,
    isVotiumFull,
    isVotiumPendingOnly,
  } = delegationStatus(props.delegateAddress);
  // Delegation status is read live from chain (App.js -> getDelegateAddress);
  // the DB is only used for the pending/voting amounts shown below.
  const isDelegatedToVotium = isVotiumFull;
  // Delegated to Votium for either the current or the upcoming round.
  const isDelegatedToVotiumEither = isVotiumCurrent || isVotiumFuture;
  const delegationStatusLabel = delegationLoading
    ? "Loading…"
    : isVotiumPendingOnly
      ? "Delegated to Votium (starts next round)"
      : isVotiumCurrent && !isVotiumFuture
        ? "Delegated to Votium (ends next round)"
        : isVotiumFuture
          ? "Delegated to Votium"
          : "Loading…";
  // Weight-sync warning: relevant for anyone delegated to Votium (current or
  // future) whose recorded delegation weight is behind their locked balance.
  const [needsWeightSync, setNeedsWeightSync] = useState(false);
  const [hasExpiredLocks, setHasExpiredLocks] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  // Keep VoteCalculator mounted while its dialog is open so refreshDelegation /
  // needsWeightSync flips cannot unmount the modal mid-flow (C1).
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);

  const showOptimizeRewards =
    !delegationLoading &&
    !isVotiumFull &&
    connectedNetwork === 1 &&
    !needsWeightSync;
  const showSetupCalculator = showOptimizeRewards || setupDialogOpen;

  useEffect(() => {
    if (!isDelegatedToVotiumEither || !props.provider || !props.address) {
      setNeedsWeightSync(false);
      setHasExpiredLocks(false);
      setSyncError(null);
      return;
    }
    let cancelled = false;
    getWeightSyncStatus(props.address, props.provider).then((s) => {
      if (cancelled) return;
      setNeedsWeightSync(s.needsSync);
      setHasExpiredLocks(s.hasExpiredLocks);
      if (!s.needsSync) setSyncError(null);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelegatedToVotiumEither, props.address]);

  const handleSync = async () => {
    if (!props.provider || !props.address) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const tx = await syncDelegation(props.provider, props.address, {
        wait: false,
      });
      await pollForReceipt(props.provider, tx.hash);
      const s = await getWeightSyncStatus(props.address, props.provider);
      setNeedsWeightSync(s.needsSync);
      setHasExpiredLocks(s.hasExpiredLocks);
    } catch (e) {
      console.log("Vote weight sync failed", e);
      setSyncError(e.message || "Sync failed.");
    }
    setSyncing(false);
  };

  // theme setting
  useEffect(() => {
    if (props.ctheme === "light") {
      setClasses(classesLight);
    } else {
      setClasses(classesDark);
    }
  }, [props.ctheme]);

  // check provider network
  useEffect(() => {
    if (props.provider !== undefined) {
      if (props.provider._network !== undefined) {
        setConnectedNetwork(props.provider._network.chainId);
        if (props.provider._network.chainId !== 1) {
          setDelegateText("Connect to Ethereum");
        } else {
          setDelegateText("Optimize Rewards");
        }
      } else if (connectedNetwork > -100 && connectedNetwork < 1) {
        // Only count down while the network is still resolving. Once a real
        // chainId is set, ignore a transient undefined _network (App recreates
        // the provider every render) so the optimize CTA doesn't flicker out.
        setTimeout(() => {
          setConnectedNetwork(connectedNetwork - 1);
        }, 100);
      }
    }
  }, [props.provider, connectedNetwork]);
  //get address amount from database
  useEffect(() => {
    async function getRefs() {
      if (props.address) {
        const addressLockedRef = ref(
          props.db,
          "delegation/users/" + props.address.toUpperCase()
        );
        setAddressLockedRef(addressLockedRef);
      } else {
        setAddressLockedRef(null);
        setRefreshAddressCheck(refreshAddressCheck + 1);
      }
    }
    async function getInfoFromDatabase() {
      if (props.db && props.address) {
        try {
          if (addressLockedRef !== null) {
            onValue(addressLockedRef, (snapshot) => {
              const data = snapshot.val();
              if (data !== null) {
                setaddressPendingAmount(
                  ethers.utils.commify(
                    (Number(data.locked) - Number(data.balance))
                      .toFixed(2)
                      .toString()
                  )
                );
                setAddressVotingAmount(
                  ethers.utils.commify(
                    Number(data.balance).toFixed(2).toString()
                  )
                );
              } else {
                console.log("Couldn't get address amount from db");
                setAddressLockedRef(null);
              }
            });
          } else {
            setRefreshAddressCheck(refreshAddressCheck + 1);
          }
        } catch {
          console.log("Couldn't get info from database");
          setRefreshAddressCheck(refreshAddressCheck + 1);
        }
      }
    }
    if (refreshAddressCheck < 50 && props.address) {
      try {
        getRefs();
        getInfoFromDatabase();
      } catch {
        if (props.provider !== undefined) {
          if (props.provider._network !== undefined) {
            console.log(props.provider._network.chainId);
          }
        }
        console.log();
        setRefreshAddressCheck(refreshAddressCheck + 1);
      }
    } else {
      console.log("Couldn't run getRefs / getInfoFromDatabase");
    }
  }, [props.db, props.provider, props.address, refreshAddressCheck]);

  //get amounts from database
  useEffect(() => {
    async function getRefs() {
      const totalDelegatedRef = ref(props.db, "delegation/totalCVX");
      setTotalDelegatedRef(totalDelegatedRef);
      const totalLockedRef = ref(props.db, "api/data/totalSupply");
      setTotalLockedRef(totalLockedRef);
      const totalveCRVRef = ref(props.db, "api/data/vecrv");
      setTotalveCRVRef(totalveCRVRef);
    }
    async function getInfoFromDatabase() {
      if (props.db) {
        try {
          onValue(totalDelegatedRef, (snapshot) => {
            const data = snapshot.val();
            if (data !== null) {
              setTotalDelegatedAmount(
                ethers.utils.commify(Number(data).toFixed(0).toString())
              );
            } else {
              console.log("Couldn't get total delegated from db");
            }
          });
          onValue(totalLockedRef, (snapshot) => {
            const data = snapshot.val();
            if (data !== null) {
              setTotalLockedAmount(
                ethers.utils.commify((data / 10 ** 18).toFixed(0).toString())
              );
            } else {
              console.log("Couldn't get total locked from db");
            }
          });
          onValue(totalveCRVRef, (snapshot) => {
            const data = snapshot.val();
            if (data !== null) {
              setTotalveCRVAmount(
                ethers.utils.commify((data / 10 ** 18).toFixed(0).toString())
              );
            } else {
              console.log("Couldn't get vecrv amount from db");
            }
          });
        } catch {
          console.log("Couldn't get info from database");
          setRefreshDB(refreshDB + 1);
        }
      }
    }
    try {
      if (refreshDB < 30) {
        getRefs();
        getInfoFromDatabase();
      }
    } catch {
      console.log("Couldn't run getRefs / getInfoFromDatabase");
    }
  }, [props.db, props.provider, refreshDB]);

  useEffect(() => {
    if (window.location.pathname === "/claim") {
      setButtonText("View Proposal");
    } else {
      setButtonText("Claim Rewards");
    }
  }, []);
  const handleHeaderButton = () => {
    if (buttonText === "Claim Rewards") {
      setButtonText("View Proposal");
    } else {
      setButtonText("Claim Rewards");
    }
  };

  return (
    <>
      <div className={classes.header}>
        <Toolbar disableGutters className={classes.toolbar}>
          {/* Header logo and links */}
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={6}>
              <img alt="votium logo" className={classes.logo} src={logo} />
            </Grid>
            <Grid
              container
              xs={6}
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item className={classes.address}>
                <WalletButton
                  provider={props.provider}
                  connect={props.connect}
                  disconnect={props.disconnect}
                  address={props.address}
                  wallet={props.wallet}
                />
                <Grid item className={classes.switchButton}>
                  <Select
                    value={window.location.pathname}
                    onChange={(event) =>
                      (window.location.pathname = event.target.value)
                    }
                    className={classes.selectMenu}
                    MenuProps={{
                      classes: { paper: classes.selectMenuItems },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {menuItems.map((item) => (
                      <MenuItem
                        value={item.value}
                        className={classes.selectMenuItems}
                        style={{
                          display:
                            item.value === window.location.pathname
                              ? "none"
                              : "block",
                        }}
                      >
                        <Grid container direction="row" alignItems="center">
                          {item.name}
                          <img
                            alt={`${item.name} logo`}
                            className={classes.vlcvxLogo}
                            src={item.logo}
                          />
                          <img
                            alt={`${item.name} logo`}
                            className={classes.curveLogoSmall}
                            src={item.smallLogo}
                          />
                        </Grid>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
        {/* Balances */}
        <Grid container justifyContent="center" alignItems="center">
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            md={8}
            xs={12}
            direction="column"
          >
            {!isYB && (
              <Grid item>
                <Typography className={classes.stakedVotiText}>
                  Eligible for Incentives
                </Typography>
                <Grid container direction="row" justifyContent="center">
                  <Grid item>
                    <img
                      alt="snapshot-logo"
                      src={cvxLogo}
                      className={classes.stakedVotiLogo}
                    ></img>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.stakedVotiAmount}>
                      {totalLockedAmount}
                    </Typography>
                  </Grid>
                  <Grid container direction="column">
                    <Grid item>
                      <Typography className={classes.totalText}>
                        {"≈ " + totalveCRVAmount + " veCRV"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item>
              <div className={classes.delegatedSpacesTitle}>
                <div className={classes.headText}>
                  <div>
                    <img
                      src={votBoxOutlined}
                      alt="outlined"
                      className={classes.headImage}
                      style={{
                        filter: isYB ? "grayscale(1)" : "none",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                  <div className={classes.imgText}>
                    <Typography className={classes.textInside}></Typography>
                  </div>
                  <div className={classes.imgTextAmount}>
                    {window.location.pathname !== "/claim" ? (
                      <Link
                        to="/claim"
                        className={classes.claimButton}
                        variant="outlined"
                        onClick={handleHeaderButton}
                      >
                        {buttonText}
                      </Link>
                    ) : (
                      <Link
                        to="/"
                        className={classes.claimButton}
                        variant="outlined"
                        onClick={handleHeaderButton}
                      >
                        {buttonText}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Grid>
            {!isYB && (
              <Grid item>
                <Typography className={classes.delegatedAmountText}>
                  Delegated Voting Power
                </Typography>
                <Grid container direction="row" justifyContent="center">
                  {addressLockedRef !== null && (
                    <Grid item>
                      <img
                        alt="snapshot-logo"
                        src={cvxLogo}
                        className={classes.stakedCVXLogo}
                      ></img>
                    </Grid>
                  )}
                  {addressLockedRef !== null && (
                    <>
                      <Grid item>
                        <Typography className={classes.stakedVotiAmount}>
                          {addressVotingAmount}
                        </Typography>
                      </Grid>
                      {addressPendingAmount !== "0.0" ? (
                        <Grid container direction="column">
                          <Grid item>
                            <Typography className={classes.pendingAmountText}>
                              Pending next round:{" "}
                              <span className={classes.pendingAmount}>
                                {addressPendingAmount}
                              </span>
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : null}
                    </>
                  )}
                  {isDelegatedToVotiumEither && needsWeightSync && (
                    <Grid item xs={12}>
                      {hasExpiredLocks ? (
                        <div
                          title="You have expired vlCVX locks. Relock or process them on Convex before your vote weight can be synced."
                          className={classes.syncWarning}
                        >
                          <span className={classes.syncWarningText}>
                            ⚠ Expired locks
                          </span>
                          <Button
                            onClick={() =>
                              window.open(
                                "https://www.convexfinance.com/lock-cvx",
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                            className={classes.syncButton}
                          >
                            Manage
                          </Button>
                        </div>
                      ) : (
                        <div
                          title="Your locked balance has increased since your delegated vote weight was last recorded. Sync to update the weight Votium votes with on your behalf."
                          className={classes.syncWarning}
                        >
                          <span className={classes.syncWarningText}>
                            {syncError
                              ? `⚠ ${syncError}`
                              : "⚠ Vote weight out of sync"}
                          </span>
                          <Button
                            onClick={handleSync}
                            disabled={syncing}
                            className={classes.syncButton}
                          >
                            {syncing ? "Syncing…" : syncError ? "Retry" : "Sync"}
                          </Button>
                        </div>
                      )}
                    </Grid>
                  )}
                  {showSetupCalculator ? (
                    <>
                      <Grid item align="center">
                        <VoteCalculator
                          setupOnly
                          noActiveVote={!props.proposal}
                          hideTrigger={!showOptimizeRewards}
                          onOpenChange={setSetupDialogOpen}
                          triggerLabel={delegateText}
                          triggerClasses={{
                            root: classes.delegateButton,
                            disabled: classes.delegateButtonDisabled,
                          }}
                          triggerDisabled={connectedNetwork !== 1}
                          triggerDisableRipple
                          ctheme={props.ctheme}
                          delegateAddress={props.delegateAddress}
                          provider={props.provider}
                          address={props.address}
                          refreshDelegation={props.refreshDelegation}
                          connect={props.connect}
                          bribesArray={[]}
                        />
                      </Grid>
                      {showOptimizeRewards && (
                        <Grid container direction="column" alignItems="center">
                          <Grid item>
                            <Typography
                              className={classes.totalTextUndelegated}
                            >
                              {"total delegated: " +
                                totalDelegatedAmount +
                                " vlCVX"}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  ) : connectedNetwork !== 1 && !isDelegatedToVotium ? (
                    <>
                      <Grid item>
                        <Typography className={classes.connectText}>
                          Connect to Ethereum to delegate
                        </Typography>
                      </Grid>
                      <Grid container direction="column">
                        <Grid item>
                          <Typography className={classes.totalText}>
                            {"From all users: " + totalDelegatedAmount}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  ) : addressLockedRef !== null ? (
                    <Grid container direction="column">
                      <Grid item>
                        <Typography className={classes.totalText}>
                          {"out of a total " + totalDelegatedAmount}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Grid item>
                        <Typography className={classes.connectText}>
                          {delegationStatusLabel}
                        </Typography>
                      </Grid>
                      <Grid container direction="column" alignItems="center">
                        <Grid item>
                          <Typography
                            className={classes.totalTextUndelegated}
                          >
                            {"total delegated: " +
                              totalDelegatedAmount +
                              " vlCVX"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid item></Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Header;
